import { useEffect } from 'react';

import type { SelectionListIdNames, TreeItem } from '@revelio/iso-utility';

import { useCurrentSkillTaxonomyVersion } from '../../../../../shared/selection-lists/hooks/useSkillTaxonomyAdapter';
import type { EntitySelections } from '../../report-builder.model';
import {
  isColumnId,
  updateReportEntitySelections,
} from './report-entity-selection.utils';
import { HIDDEN_REPORT_FIELDS } from './report-hidden-columns';
import type { ReportEntityConfig } from './utils';

const createSkillTaxonomyTreeItem = (
  keyword:
    | typeof HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_VERSION
    | typeof HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_NAME,
  value: string
): TreeItem<string> => {
  return {
    id: value,
    parentId: undefined,
    selectionListId: keyword as unknown as SelectionListIdNames,
    item: {
      id: value,
      label: value,
      shortName: value,
    },
    children: [],
  };
};

/**
 * Injects hidden skill taxonomy metadata fields if requested by the report config:
 * - skill_taxonomy_version
 * - skill_taxonomy_name
 *
 * Derives taxonomy metadata from the registry-backed default skill taxonomy
 * (name + latest version).
 */
export const useReportsSkillTaxonomy = (
  entityConfigs: ReportEntityConfig | null,
  selectedReportId?: number,
  _entitySelections?: EntitySelections
) => {
  // Default skill taxonomy name + version, sourced from the registry
  // (TaxonomyList + TaxonomyLatestVersion) the same way the rest of the skill
  // taxonomy surfaces resolve theirs. Target taxonomy id 0 explicitly — the
  // default the report builder always uses (matching `useReportsCustomSkills(0)`
  // and the old `taxonomyId: 0`). Resolving by `is_latest` would be ambiguous:
  // every taxonomy at the latest version is `is_latest`, so a same-version
  // company taxonomy ordered ahead of the default would inject the wrong name.
  const { taxonomies } = useCurrentSkillTaxonomyVersion();
  const defaultTaxonomy = taxonomies?.find((taxonomy) => taxonomy.id === 0);
  const taxonomyName = defaultTaxonomy?.name;
  const taxonomyVersion = defaultTaxonomy?.version;

  useEffect(() => {
    if (
      !entityConfigs ||
      selectedReportId === undefined ||
      selectedReportId === null
    ) {
      return;
    }

    // Only proceed if at least one metadata piece exists
    if (!taxonomyName && !taxonomyVersion) return;

    // Inject version if requested
    if (taxonomyVersion) {
      const versionColumn = entityConfigs.columns?.find((c) =>
        isColumnId(HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_VERSION, c)
      );
      if (versionColumn) {
        updateReportEntitySelections({
          reportId: selectedReportId,
          reportEntity: versionColumn.id,
          selections: {
            [HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_VERSION]:
              createSkillTaxonomyTreeItem(
                HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_VERSION,
                taxonomyVersion
              ),
          },
        });
      }
    }

    // Inject name if requested
    if (taxonomyName) {
      const nameColumn = entityConfigs.columns?.find((c) =>
        isColumnId(HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_NAME, c)
      );
      if (nameColumn) {
        updateReportEntitySelections({
          reportId: selectedReportId,
          reportEntity: nameColumn.id,
          selections: {
            [HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_NAME]:
              createSkillTaxonomyTreeItem(
                HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_NAME,
                taxonomyName
              ),
          },
        });
      }
    }
  }, [entityConfigs, selectedReportId, taxonomyName, taxonomyVersion]);
};
